import { useEffect, useRef, type JSX } from 'react';
import { useEngine } from '../../state/engineStore';
import type { TrendSeries } from '../../engine/core/trends';

interface Props {
  tags: { tag: string; colour: string }[];
  windowS: number;
  height?: number;
  rangeLo?: number;
  rangeHi?: number;
  /** Horizontal reference lines in engineering units (alarm limits, setpoint). */
  marks?: { value: number; colour: string; dashed?: boolean }[];
}

/** Canvas 2D mini-trend. Kept deliberately cheap: it redraws at the snapshot rate. */
export function MiniTrend({ tags, windowS, height = 76, rangeLo, rangeHi, marks = [] }: Props): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);
  const seriesRef = useRef<TrendSeries[]>([]);
  const tick = useEngine((s) => s.tick);
  const simTime = useEngine((s) => s.snap?.simTime ?? 0);
  const requestTrend = useEngine((s) => s.requestTrend);
  const pending = useRef(false);

  useEffect(() => {
    if (pending.current) return;
    pending.current = true;
    void requestTrend(tags.map((t) => t.tag), Math.max(0, simTime - windowS), simTime, 400).then((s) => {
      seriesRef.current = s;
      pending.current = false;
      draw();
    });
    // Refresh on every few snapshots; the promise guard keeps at most one in flight.
  }, [tick >> 2, windowS, tags.map((t) => t.tag).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  function draw(): void {
    const cv = ref.current;
    if (!cv) return;
    const dpr = window.devicePixelRatio || 1;
    const w = cv.clientWidth;
    const h = cv.clientHeight;
    if (cv.width !== w * dpr || cv.height !== h * dpr) {
      cv.width = w * dpr;
      cv.height = h * dpr;
    }
    const g = cv.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);

    const css = getComputedStyle(document.documentElement);
    g.fillStyle = css.getPropertyValue('--bg-deep') || '#222';
    g.fillRect(0, 0, w, h);

    const series = seriesRef.current;
    let lo = rangeLo ?? Number.POSITIVE_INFINITY;
    let hi = rangeHi ?? Number.NEGATIVE_INFINITY;
    if (rangeLo === undefined || rangeHi === undefined) {
      for (const s of series) {
        for (let i = 0; i < s.mean.length; i++) {
          lo = Math.min(lo, s.min[i] as number);
          hi = Math.max(hi, s.max[i] as number);
        }
      }
      for (const m of marks) {
        lo = Math.min(lo, m.value);
        hi = Math.max(hi, m.value);
      }
      if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi - lo < 1e-9) {
        lo = (lo || 0) - 1;
        hi = (hi || 0) + 1;
      }
      const pad = (hi - lo) * 0.08;
      lo -= pad;
      hi += pad;
    }
    const t0 = Math.max(0, simTime - windowS);
    const X = (t: number): number => ((t - t0) / Math.max(windowS, 1e-6)) * w;
    const Y = (v: number): number => h - ((v - lo) / Math.max(hi - lo, 1e-9)) * h;

    g.strokeStyle = css.getPropertyValue('--line-dim') || '#555';
    g.globalAlpha = 0.35;
    g.beginPath();
    for (let i = 1; i < 4; i++) {
      g.moveTo(0, (h * i) / 4);
      g.lineTo(w, (h * i) / 4);
    }
    g.stroke();
    g.globalAlpha = 1;

    for (const m of marks) {
      g.strokeStyle = m.colour;
      g.globalAlpha = 0.8;
      g.setLineDash(m.dashed ? [4, 3] : []);
      g.beginPath();
      g.moveTo(0, Y(m.value));
      g.lineTo(w, Y(m.value));
      g.stroke();
      g.setLineDash([]);
      g.globalAlpha = 1;
    }

    series.forEach((s, i) => {
      const colour = tags[i]?.colour ?? '#fff';
      // Min/max envelope first, so a spike inside a decimated bucket is still visible.
      if (s.periodS > 1) {
        g.fillStyle = colour;
        g.globalAlpha = 0.18;
        g.beginPath();
        for (let k = 0; k < s.t.length; k++) g.lineTo(X(s.t[k] as number), Y(s.max[k] as number));
        for (let k = s.t.length - 1; k >= 0; k--) g.lineTo(X(s.t[k] as number), Y(s.min[k] as number));
        g.closePath();
        g.fill();
        g.globalAlpha = 1;
      }
      g.strokeStyle = colour;
      g.lineWidth = 1.4;
      g.beginPath();
      for (let k = 0; k < s.t.length; k++) {
        const px = X(s.t[k] as number);
        const py = Y(s.mean[k] as number);
        if (k === 0) g.moveTo(px, py);
        else g.lineTo(px, py);
      }
      g.stroke();
    });
  }

  useEffect(draw);

  return <canvas ref={ref} style={{ width: '100%', height, display: 'block', borderRadius: 3 }} />;
}
